import { Component, ElementRef, HostListener, Input, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { Subscription } from 'rxjs';
import * as L from 'leaflet';

import { ProjectsService } from '../../../services/projects.service';
import * as Projects from '../../../shared/projects.model';
import { LanguageService } from '../../../services/language.service';
import { AuthService } from '../../../services/auth.service';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { User } from 'src/app/shared/user.model';
import { ModalService } from '../../../shared/services/modal/modal.service';
import { AboutDialogComponent } from '../about-dialog/about-dialog.component';
import { ModalConstants } from '../../../shared/services/modal/modal.constants';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-project-description',
  templateUrl: './project-description.component.html',
  styleUrls: ['./project-description.component.scss']
})
export class ProjectDescriptionComponent implements OnInit {

  project: Projects.FullProject;
  isLogged = false;
  isPledge = false;
  currentIndex = 0;
  readMoreClicked = false;
  screenWidth: number;
  gaugeLeftMargin = 1;
  direction: string;
  widthDesktopSubs: Subscription;
  gaugeStatus: number; /* 0 if target_kpi not reached, 1 if target_kpi reached no second_target_kpi,
                          2 if target_kpi reached second_target_kpi */

  @Input() isMobile;
  @ViewChildren('video') htmlVideos: QueryList<ElementRef>;
  @ViewChild('carousel') carouselRef: ElementRef;
  @ViewChild('slider') sliderRef: ElementRef;

  carousel: HTMLElement;
  slider: HTMLElement;
  defaultTouch = { x: 0, y: 0, time: 0 };

  constructor(private projectsService: ProjectsService,
              private languageService: LanguageService,
              private translate: TranslateService,
              private authService: AuthService,
              private breakpointObserver: BreakpointObserver,
              private modal: ModalService) {
  }

  get currentLanguage() {
    return this.languageService.getUserUsedLanguage();
  }

  @HostListener('touchstart', ['$event'])
  @HostListener('touchend', ['$event'])
  handleTouch(event) {
    if (event.target.classList.contains('slide')) {
      const touch = event.touches[0] || event.changedTouches[0];

      if (event.type === 'touchstart') {
        this.defaultTouch.x = touch.pageX;
        this.defaultTouch.time = event.timeStamp;
      } else if (event.type === 'touchend') {
        const deltaX = touch.pageX - this.defaultTouch.x;
        const deltaTime = event.timeStamp - this.defaultTouch.time;

        // Simulate a swipe -> less than 500 ms and more than 60 px
        if (deltaTime < 500) {
          if (Math.abs(deltaX) > 60) {
            deltaX > 0 ? this.prev(this.project.media.length) : this.next(this.project.media.length);
          }
        }
      }
    }
  }

  ngOnInit(): void {
    this.screenWidth = window.innerWidth;
    this.projectsService.fullProjectInfo.subscribe({
      next: (project) => {
        this.project = project;
        this.calculateGaugeLabelLeftMargin();
        if (this.project.progress >= this.project.target_kpi) {
          if (this.project.second_target_kpi) {
            this.gaugeStatus = 2;
          } else {
            this.gaugeStatus = 1;
          }
        } else {
          this.gaugeStatus = 0;
        }
        if (this.project) {
          const corner1 = L.latLng(-90, -180);
          const corner2 = L.latLng(90, 180);
          const mapOptions = {
            zoomControl: false,
            minZoom: 1,
            maxZoom: 7,
            maxBounds: L.latLngBounds(corner1, corner2),
            maxBoundsViscosity: 1
          };
          const map = L.map('map', mapOptions).setView([this.project.latitude, this.project.longitude], 3);
          L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}', {
              attribution: '&copy; <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          }).addTo(map);
          const markerOptions = {
            radius: 8,
            stroke: true,
            weight: 1,
            color: '#fff',
            fillOpacity: 1,
            fillColor: '#34D1B0'
          };
          const marker = L.circleMarker([this.project.latitude, this.project.longitude], markerOptions).addTo(map);
        }
      }
    });
    this.widthDesktopSubs = this.breakpointObserver
      .observe(['(max-width: 1100px)'])
      .subscribe((stateVar: BreakpointState) => {
        this.isMobile = stateVar.matches;
      });
    this.authService.getCurrentUser().subscribe({
      next: (user: User) => {
        this.isLogged = true;
        this.isPledge = user.pledge_id !== null;
      }
    });
  }

  roundPercentage(value: number) {
    return Math.ceil(value);
  }

  isYoutubeVideo(url: string): boolean {
    return url.includes('www.youtube.com');
  }

  playVideo(element: HTMLVideoElement): void {
    element.play();
  }

  pauseVideo(element: HTMLVideoElement): void {
    element.pause();
  }

  pauseHTMLVideo(index): void {
    this.htmlVideos.map((video: ElementRef) => {
      if (video.nativeElement.id === `video${ index }`) {
        video.nativeElement.pause();
      }
    });
  }

  isPaused(element: HTMLVideoElement): boolean {
    return element.paused;
  }

  isImage(url: string): boolean {
    const formats = ['.jpg', '.jpeg', '.png', '.webp', '.gif'];
    return formats.some(item => url.includes(item));
  }

  next(mediaLength: number) {
    this.direction = 'next';
    if (mediaLength >= 2) {
      if (this.currentIndex === mediaLength - 1) {
        this.currentIndex = 0;
      } else {
        this.currentIndex++;
      }
    }
  }

  prev(mediaLength: number) {
    if (mediaLength >= 2) {
      this.pauseHTMLVideo(this.currentIndex);
      if (this.currentIndex === 0) {
        this.currentIndex = mediaLength - 1;
      } else {
        this.currentIndex--;
      }
    }
  }

  toggleReadMore() {
    this.readMoreClicked = !this.readMoreClicked;
  }

  calculateGaugeLabelLeftMargin(): void {
    const margin = 100 * this.project.progress / this.project.target_kpi;
    this.gaugeLeftMargin = margin > 1 ? margin : 1;
  }

  openAboutDialog(type: string, name: string, logo: string, additionalData: any[], website: any, description: any, standards: string[]) {
    const data = { type, name, logo, additionalData, website, description, standards };
    this.modal.openFixedModal(data, AboutDialogComponent,
      [ModalConstants.FULLSCREEN_VISIBLE_HEADER_PANEL_CLASS, 'animate__animated', 'animate__slideInUp'], 1100);
  }

  getTooltipText() {
    return this.translate.instant('PROJECT_PAGE.first_achieved') + this.project.target_kpi + ' ' + this.project.main_kpi_unit[this.currentLanguage];
  }
}
